import React, { useCallback, useContext, useState } from "react";
import UserContext from "../../context/UserContext";
import axios from "axios";
import { useScreenWidth } from "../../hooks/useScreenWidth";
import { Form, InputGroup, Button } from "react-bootstrap";
import "./LoginPage.scss";
import { EyeOutlined, EyeInvisibleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GPLogo from "../../assets/images/logo/gp-logo-trans-w100.webp";
import LoginPageDesktop from "./LoginPageDesktop";
import LoginButton from "./reusable/LoginButton";
import LoginForgotPassword from "./reusable/LoginForgotPassword";
import MandatoryText from "./reusable/MandatoryText";
import { ToastContainer, toast } from "react-toastify";
import useTranslation from "../../hooks/useTranslation";
import { completeAuthentication } from "../../utils/completeAuthentication";

function PasswordField({ value, onChange }) {
  const [showPassword, setShowPassword] = useState(false);
  const { t } = useTranslation("loginRelatedPages", true);

  return (
    <InputGroup>
      <Form.Control
        type={showPassword ? "text" : "password"}
        className="form-control"
        id="password"
        placeholder={t("minPassLengthPlaceholder")}
        value={value}
        onChange={onChange}
      />
      <Button
        onClick={() => setShowPassword(!showPassword)}
        variant="outline-secondary"
      >
        {showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
      </Button>
    </InputGroup>
  );
}

function LoginPage() {
  const { setUser } = useContext(UserContext);
  const [isChecked, setIsChecked] = useState(false);
  const screenWidth = useScreenWidth();
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL_FOR_FRONTEND;
  const [formData, setFormData] = useState({ email: "", password: "" });
  const { t } = useTranslation("loginRelatedPages", true);

  const handleEmailChange = useCallback((e) => {
    setFormData((prevFormData) => ({ ...prevFormData, email: e.target.value }));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    setFormData((prevFormData) => ({
      ...prevFormData,
      password: e.target.value,
    }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    /*
    if (!isChecked) {
      toast.error(t("privacyPolicyCheckBoxError"));
      return;
    }
    */
    if (formData.email === "" || formData.password === "") {
      toast.error(t("fillAllFields"));
      return;
    }

    const useremail = formData.email;
    const userpassword = formData.password;

    try {
      const response = await axios.post(`${backendUrl}/login`, {
        useremail,
        userpassword,
        readDataProtection: isChecked,
      });

      // Check if 2FA is required
      if (response.data.requireTotp) {
        // Navigate to 2FA page instead of showing inline
        navigate("/verify-2fa", {
          state: { partialAuth: response.data },
        });
        return; // Stop further execution
      }

      // Normal login flow continues if 2FA isn't required
      completeAuthentication(response.data, setUser, navigate, t);
    } catch (error) {
      if (error.response) {
        if (error.response.status === 401) {
          toast.error(t("loginError"));
        }
        if (error.response.status === 403) {
          console.log("403-as hiba");
          const { message } = error.response.data;
          console.log("403 message: ", message);
          toast.error(t("loginFailedCloseAccount"));
        }

        if (error.response.status === 400) {
          toast.error(t("privacyPolicyCheckBoxError"));
        }

        if (error.response.status === 500) {
          console.log("500-as hiba");
          toast.error(t("loginFailedServer"));
        }
      } else {
        if (error.message === "Network Error") {
          toast.error(t("loginFailedNetwork"));
        }
        console.log("Login error: ", error);
        toast.error(t("loginFailedMessage"));
      }
    }
  };

  return (
    <>
      {screenWidth < 1024 ? (
        <div className="container login-page__container">
          <ToastContainer />
          <div className="login-page__form">
            <img src={GPLogo} alt="logo" />
            <p className="login-page__form-subtitle">
            {t("loginSubtitle")}.
            </p>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="form-group">
                <Form.Label htmlFor="email">E-mail</Form.Label>
                <Form.Control
                  type="email"
                  className="form-control"
                  id="email"
                  aria-describedby="emailHelp"
                  placeholder="nev@minta.com"
                  value={formData.email}
                  onChange={handleEmailChange}
                />
              </Form.Group>
              <Form.Group className="form-group">
                <Form.Label htmlFor="password">{t("password")}</Form.Label>
                <PasswordField
                  value={formData.password}
                  onChange={handlePasswordChange}
                />
              </Form.Group>
              <MandatoryText
                isChecked={isChecked}
                setIsChecked={setIsChecked}
              />
              <LoginButton handleSubmit={handleSubmit} />
            </Form>
            <LoginForgotPassword />
          </div>
        </div>
      ) : (
        <LoginPageDesktop
          handleSubmit={handleSubmit}
          PasswordField={PasswordField}
          isChecked={isChecked}
          setIsChecked={setIsChecked}
          handleEmailChange={handleEmailChange}
          handlePasswordChange={handlePasswordChange}
        />
      )}
    </>
  );
}

export default LoginPage;
